import React, { useEffect, useState } from 'react';
import './SolutionsWorkflowStepper.css';

const STEPS = [
  {
    meta: '01 · INGEST',
    label: 'Finding received',
    iconSrc: '/images/solutions/workflow-autonomous-triage-01-ingest.svg',
  },
  {
    meta: '02 · INVESTIGATE',
    label: 'Investigate',
    iconSrc: '/images/solutions/workflow-autonomous-triage-02-investigate.svg',
  },
  {
    meta: '03 · TRIAGE',
    label: 'Gather triage log',
    iconSrc: '/images/solutions/workflow-autonomous-triage-03-triage.svg',
  },
  {
    meta: '04 · ROOT CAUSE',
    label: 'Identify root cause',
    iconSrc: '/images/solutions/workflow-autonomous-triage-04-root-cause.svg',
  },
  {
    meta: '05 · DECIDE',
    label: 'Decide the outcome',
    iconSrc: '/images/solutions/workflow-autonomous-triage-05-decide.svg',
  },
];

const STEP_MS = 3200;

const SolutionsWorkflowStepper = ({
  titleId = 'solutions-workflow-heading',
  theme = 'dark',
  showEyebrow = true,
  eyebrowText = 'Autonomous triage',
  showTitle = true,
  includeSrTitle = true,
  titleText = 'From finding to decision',
  activeStepIndex: controlledIndex,
  onActiveStepChange,
}) => {
  const [internalIndex, setInternalIndex] = useState(0);
  const [reduceMotion, setReduceMotion] = useState(false);
  const controlled = controlledIndex !== undefined;
  const activeIndex = controlled ? controlledIndex : internalIndex;
  const demoCollapseMotion = reduceMotion && !controlled;

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const sync = () => setReduceMotion(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  useEffect(() => {
    if (controlled || reduceMotion) return undefined;
    const id = window.setInterval(() => {
      setInternalIndex((i) => (i + 1) % STEPS.length);
    }, STEP_MS);
    return () => window.clearInterval(id);
  }, [controlled, reduceMotion]);

  const lastIndex = STEPS.length - 1;
  const progress =
    demoCollapseMotion ? 1 : lastIndex === 0 ? 0 : activeIndex / lastIndex;

  const setStep = (i) => {
    if (controlled) {
      onActiveStepChange?.(i);
    } else {
      setInternalIndex(i);
    }
  };

  const interactive = Boolean(onActiveStepChange);

  return (
    <div
      className={`solutions-stepper solutions-stepper--${theme}${
        reduceMotion ? ' solutions-stepper--reduce-motion' : ''
      }`}
    >
      {showEyebrow && eyebrowText ? (
        <p className="solutions-stepper__eyebrow">{eyebrowText}</p>
      ) : null}
      {showTitle ? (
        <h2 className="solutions-stepper__title" id={titleId}>
          {titleText}
        </h2>
      ) : includeSrTitle ? (
        <h2 id={titleId} className="solutions-stepper__sr-title">
          {titleText}
        </h2>
      ) : null}
      <div className="solutions-stepper__scroll">
        <div className="solutions-stepper__frame">
          <div className="solutions-stepper__rail" aria-hidden="true">
            <div className="solutions-stepper__rail-bg" />
            <div
              className="solutions-stepper__rail-fill"
              style={{ '--stepper-progress': String(progress) }}
            />
          </div>
          <ol className="solutions-stepper__steps">
            {STEPS.map((step, i) => {
              let state = 'upcoming';
              if (demoCollapseMotion) {
                state = 'complete';
              } else if (i < activeIndex) {
                state = 'complete';
              } else if (i === activeIndex) {
                state = 'active';
              }

              const stepBody = (
                <>
                  <span className="solutions-stepper__node-wrap">
                    <span className="solutions-stepper__node">
                      <img
                        src={step.iconSrc}
                        alt=""
                        width={48}
                        height={48}
                        loading="lazy"
                        decoding="async"
                      />
                    </span>
                  </span>
                  <span className="solutions-stepper__meta">{step.meta}</span>
                  <span className="solutions-stepper__label">{step.label}</span>
                </>
              );

              return (
                <li
                  key={step.meta}
                  className={`solutions-stepper__step solutions-stepper__step--${state}`}
                  aria-current={!demoCollapseMotion && i === activeIndex ? 'step' : undefined}
                >
                  {interactive ? (
                    <button
                      type="button"
                      className="solutions-stepper__step-trigger"
                      aria-pressed={i === activeIndex}
                      aria-label={`${step.meta}: ${step.label}`}
                      onClick={() => setStep(i)}
                      onMouseEnter={() => setStep(i)}
                      onFocus={() => setStep(i)}
                    >
                      {stepBody}
                    </button>
                  ) : (
                    stepBody
                  )}
                </li>
              );
            })}
          </ol>
        </div>
      </div>
    </div>
  );
};

export default SolutionsWorkflowStepper;
